import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx'
import { copyWithToast } from '../../utils/clipboard'

interface DraftClaimsDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

const DraftClaimsDisplay: React.FC<DraftClaimsDisplayProps> = ({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [showRawData, setShowRawData] = useState(false)
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  // Parse JSON if result is a string
  let parsedResult = result
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result)
    } catch (e) {
      // If parsing fails, return null for self-detection
      return null;
    }
  }

  // Handle both direct result and nested output structure
  const claims = parsedResult?.output || parsedResult

  // Paragraph mode detection
  const isParagraphMode = claims && typeof claims === 'object' && (
    claims.output_format === 'paragraphs' || !!claims.claims_text
  );

  // Self-detection: check if this looks like draft claims
  // Draft claims should have independent_claims or dependent_claims arrays, or claim_dependencies
  // but NOT the structural_variations/functional_variations pattern of OtherEmbodiments
  const isDraftClaims = isParagraphMode || (claims && typeof claims === 'object' && (
    claims.independent_claims !== undefined ||
    claims.dependent_claims !== undefined ||
    claims.claim_dependencies !== undefined
  ) && !(
    // Exclude if it looks like OtherEmbodiments
    claims.structural_variations !== undefined ||
    claims.functional_variations !== undefined ||
    claims.implementation_alternatives !== undefined
  ));

  if (!isDraftClaims) {
    return null; // Not draft claims, let other components handle it
  }

  if (!claims || typeof claims !== 'object') {
    return null;
  }

  // ---- Paragraph mode render ----
  if (isParagraphMode) {
    const claimsText: string = claims.claims_text || '';

    const exportParagraphsToDocx = async () => {
      try {
        const paras = claimsText.split('\n\n').filter((p: string) => p.trim());
        const docChildren: any[] = paras.map((para: string) => {
          const trimmed = para.trim();
          const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
          if (isHeading) {
            return new Paragraph({ text: trimmed, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 200 } });
          }
          return new Paragraph({ text: trimmed, spacing: { after: 200, line: 360 } });
        });
        const doc = new Document({ sections: [{ properties: {}, children: docChildren }] });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'draft-claims.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast?.('Draft Claims exported to Word document');
      } catch (error) {
        console.error('Error exporting paragraphs to DOCX:', error);
        showToast?.('Error exporting Draft Claims');
      }
    };

    const renderParagraphs = () =>
      claimsText.split('\n\n').filter((p: string) => p.trim()).map((para: string, idx: number) => {
        const trimmed = para.trim();
        const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
        if (isHeading) {
          return <p key={idx} className={`font-semibold mt-4 mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{trimmed}</p>;
        }
        return <p key={idx} className={`leading-relaxed mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{trimmed}</p>;
      });

    return (
      <div className="space-y-4">
        {showHeader && (
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Draft Claims</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full paragraph format</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportParagraphsToDocx} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
                <Download className="w-4 h-4" />Export DOCX
              </button>
              <button onClick={() => copyToClipboard(claimsText)} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                <Copy className="w-4 h-4" />Copy All
              </button>
            </div>
          </div>
        )}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          {claimsText ? renderParagraphs() : (
            <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No claims text available.</p>
          )}
        </div>
      </div>
    );
  }
  // ---- End paragraph mode render ----

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    independent_claims: { 
      title: 'Independent Claims', 
      emoji: '🔑', 
      type: 'claims',
      description: 'Primary claims that stand alone'
    },
    dependent_claims: { 
      title: 'Dependent Claims', 
      emoji: '🔗', 
      type: 'claims',
      description: 'Claims that depend on other claims'
    },
    claim_dependencies: { 
      title: 'Claim Dependencies', 
      emoji: '🌳', 
      type: 'dependencies',
      description: 'Visual representation of claim relationships'
    },
    claim_scope: { 
      title: 'Claim Scope', 
      emoji: '🎯', 
      type: 'object',
      description: 'Explanation of each claim\'s scope'
    },
    key_limitations: { 
      title: 'Key Limitations', 
      emoji: '🔧', 
      type: 'object',
      description: 'Critical limitations in each claim'
    },
    alternative_embodiments: { 
      title: 'Alternative Embodiments', 
      emoji: '🔄', 
      type: 'array',
      description: 'Suggested alternative claim approaches'
    }
  }

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Draft Claims",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const value = claims[sectionKey];

        // Skip empty sections
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && Object.keys(value).length === 0)) {
          return;
        }

        // Section heading
        children.push(
          new Paragraph({
            text: config.title,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );

        if (config.type === 'claims' && Array.isArray(value)) {
          value.forEach((claim: any) => {
            // Claim number and type
            const claimHeader = `Claim ${claim.claim_number}${claim.claim_type ? ` (${claim.claim_type})` : ''}`;
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: claimHeader,
                    bold: true,
                  }),
                ],
                spacing: { before: 200, after: 100 },
              })
            );

            // Dependency info
            if (claim.dependency && claim.dependency.length > 0) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Depends on: ${claim.dependency.join(', ')}`,
                      italics: true,
                    }),
                  ],
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }

            // Claim text
            children.push(
              new Paragraph({
                text: claim.claim_text,
                spacing: { after: 200, line: 360 },
                indent: { left: 360 },
              })
            );
          });
        } else if (config.type === 'object' && typeof value === 'object') {
          Object.entries(value).forEach(([key, val]) => {
            const valText = Array.isArray(val) ? val.join(', ') : String(val);
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `${key}: `, bold: true }),
                  new TextRun({ text: valText }),
                ],
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          });
        } else if (config.type === 'array' && Array.isArray(value)) {
          value.forEach((item) => {
            const itemText = typeof item === 'object' ? JSON.stringify(item) : String(item);
            children.push(
              new Paragraph({
                text: `• ${itemText}`,
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          });
        } else if (config.type === 'dependencies' && typeof value === 'object') {
          Object.entries(value).forEach(([claimNum, deps]) => {
            const depText = Array.isArray(deps) && deps.length > 0 ? deps.join(', ') : 'Independent';
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Claim ${claimNum}: `, bold: true }),
                  new TextRun({ text: depText }),
                ],
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          });
        }
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'draft-claims.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Draft Claims exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Draft Claims to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Draft Claims');
      }
    }
  }

  const formatFieldValue = (field, value, config) => {
    if (!value) return 'Not specified'
    
    if (config.type === 'claims' && Array.isArray(value)) {
      return (
        <div className="space-y-3">
          {value.map((claim, index) => (
            <div key={index} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`font-bold px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                  Claim {claim.claim_number}
                </span>
                {claim.claim_type && (
                  <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800'}`}>
                    {claim.claim_type}
                  </span>
                )}
                {claim.dependency && claim.dependency.length > 0 && (
                  <span className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-orange-800 text-orange-200' : 'bg-orange-100 text-orange-800'}`}>
                    Depends on: {claim.dependency.join(', ')}
                  </span>
                )}
              </div>
              <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {claim.claim_text}
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    if (config.type === 'object' && typeof value === 'object') {
      return (
        <div className="space-y-2">
          {Object.entries(value).map(([key, val]: [string, any]) => (
            <div key={key} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <span className="font-semibold">{key}: </span>
              <span>{Array.isArray(val) ? val.join(', ') : String(val)}</span>
            </div>
          ))}
        </div>
      )
    }
    
    if (config.type === 'dependencies' && typeof value === 'object') {
      return (
        <div className="space-y-2">
          {Object.entries(value).map(([claimNum, deps]) => (
            <div key={claimNum} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <span className="font-semibold">Claim {claimNum}: </span>
              <span>{Array.isArray(deps) && deps.length > 0 ? `Depends on ${deps.join(', ')}` : 'Independent'}</span>
            </div>
          ))}
        </div>
      )
    }
    
    if (config.type === 'array' && Array.isArray(value)) {
      return (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {typeof item === 'object' ? JSON.stringify(item) : item}
            </li>
          ))}
        </ul>
      )
    }
    
    return value
  }

  const renderSection = (sectionKey, config) => {
    const value = claims[sectionKey]
    const hasData = value && (
      (config.type === 'array' && Array.isArray(value) && value.length > 0) ||
      (config.type === 'object' && typeof value === 'object' && Object.keys(value).length > 0) ||
      (config.type === 'claims' && Array.isArray(value) && value.length > 0) ||
      (config.type === 'dependencies' && typeof value === 'object' && Object.keys(value).length > 0)
    )
    
    if (!hasData) return null

    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.description}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-sm font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    {config.title}
                  </span>
                  <button
                    onClick={() => {
                      const text = config.type === 'array' && Array.isArray(value) ? value.join('\n') :
                            config.type === 'object' ? JSON.stringify(value, null, 2) :
                            JSON.stringify(value, null, 2);
                      copyToClipboard(text);
                    }}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    <Copy className="w-3 h-3" />
                    Copy Field
                  </button>
                </div>
                <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {formatFieldValue(sectionKey, value, config)}
                </div>
              </div>
            </div>

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  const text = `${config.title}: ${config.type === 'array' && Array.isArray(value) ? value.join('\n- ') :
                        config.type === 'object' ? JSON.stringify(value, null, 2) :
                        JSON.stringify(value, null, 2)}`;
                  copyToClipboard(text);
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
              {addToResources && (
                <button
                  onClick={() => {
                    const sectionText = `# ${config.title}\n\n${config.type === 'array' && Array.isArray(value) ? '- ' + value.join('\n- ') : 
                                      config.type === 'object' ? JSON.stringify(value, null, 2) : 
                                      JSON.stringify(value, null, 2)}`
                    addToResources({
                      type: 'claims_section',
                      title: config.title,
                      content: sectionText,
                      source: 'Draft Claims Task'
                    })
                    showToast?.('Section added to resources!')
                  }}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'}`}
                >
                  Add to Resources
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Get sections with data
  const sectionsWithData = Object.keys(sectionConfigs).filter(sectionKey => {
    const value = claims[sectionKey]
    const config = sectionConfigs[sectionKey]
    return value && (
      (config.type === 'array' && Array.isArray(value) && value.length > 0) ||
      (config.type === 'object' && typeof value === 'object' && Object.keys(value).length > 0) ||
      (config.type === 'claims' && Array.isArray(value) && value.length > 0) ||
      (config.type === 'dependencies' && typeof value === 'object' && Object.keys(value).length > 0)
    )
  })
  
  const allSectionsText = Object.entries(sectionConfigs)
    .filter(([sectionKey, config]) => {
      const value = claims[sectionKey]
      return value && (
        (config.type === 'array' && Array.isArray(value) && value.length > 0) ||
        (config.type === 'object' && typeof value === 'object' && Object.keys(value).length > 0) ||
        (config.type === 'claims' && Array.isArray(value) && value.length > 0) ||
        (config.type === 'dependencies' && typeof value === 'object' && Object.keys(value).length > 0)
      )
    })
    .map(([sectionKey, config]) => {
      const value = claims[sectionKey]
      const formattedValue = config.type === 'array' && Array.isArray(value) ? '- ' + value.join('\n- ') : 
                            config.type === 'object' ? JSON.stringify(value, null, 2) : 
                            JSON.stringify(value, null, 2)
      return `# ${config.title}\n\n${formattedValue}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Draft Claims
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {sectionsWithData.length} section{sectionsWithData.length !== 1 ? 's' : ''} with content
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                title="Export claims as Word document"
              >
                <Download className="w-4 h-4" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
              <button
                onClick={() => setShowRawData(!showRawData)}
                className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                title="Toggle raw data display"
              >
                {showRawData ? 'Hide Raw' : 'Show Raw'}
              </button>
            </div>
          </div>

          {/* Raw Data Display */}
          {showRawData && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Raw Data
                </h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
                  className={`text-sm px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                >
                  <Copy className="w-3 h-3" />
                  Copy Raw Data
                </button>
              </div>
              <pre className={`text-xs overflow-auto max-h-96 p-3 rounded ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-800'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                sectionsWithData.forEach(sectionKey => {
                  allExpanded[sectionKey] = true
                })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                sectionsWithData.forEach(sectionKey => {
                  allCollapsed[sectionKey] = false
                })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {Object.entries(sectionConfigs).map(([sectionKey, config]) => renderSection(sectionKey, config))}

        {/* No content message */}
        {sectionsWithData.length === 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No claims sections contain data to display.
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftClaimsDisplay